import React, { useState, useEffect } from 'react';
import { ChevronRight, ChevronLeft, X } from 'lucide-react';
import Confetti from 'react-confetti';
import "./FirstTimeUserGuide.css"

const FirstTimeUserGuide = ({ 
  isVisible, 
  onComplete, 
  currentVoice,
  onStartCall 
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  
  // Reset to step 0 whenever the guide becomes visible
  useEffect(() => {
    if (isVisible) {
      setCurrentStep(0);
      setShowConfetti(false);
    }
  }, [isVisible]);
  
const steps = [
  {
    title: "Welcome to Preptune!",
    description: "Feel free to leave & come back whenever you want — your conversations will always be saved.",
    highlight: "null",
    visual: {
      type: "icon-demo",
      action: "You have 60-Days Trial, with 20 mins session",
      iconColor: "#0066cc",
      bgColor: "#e5f3fdff",
      iconType: "clock"
    }
  },
  {
    title: "Start & End Your Call",
    description: "Click the icon above to start a call with Preptune. When you're done talking, click the phone icon again to hang up.",
    highlight: ".ai-icon-io",
    visual: {
      type: "dual-icon-demo",
      icons: [
        {
          action: "Start Call",
          iconColor: "#0066cc",
          bgColor: "#e5f3fdff",
          iconType: "phone"
        },
        {
          action: "End Call",
          iconColor: "#ef4444",
          bgColor: "#fef2f2",
          iconType: "phone-off"
        }
      ]
    }
  },
  {
    title: "Restart Conversation",
    description: "Click this icon anytime you want Preptune to start a new conversation from scratch.",
    highlight: ".interrupt-icon",
    visual: {
      type: "icon-demo",
      action: "Click to Restart",
      iconColor: "#7576768a",
      bgColor: "#cccdcd89",
      iconType: "refresh"
    }
  },
  {
    title: "Take Notes & Journal",
    description: "During or after your call, click the journal icon to have Preptune automatically take notes.",
    highlight: ".journal-button",
    visual: {
      type: "icon-demo",
      action: "Click to Start Journaling",
      iconColor: "#4fd55aff",
      bgColor: "#ecfbe7ff",
      iconType: "journal"
    }
  },
  {
    title: "Save Your Journal",
    description: "After your call ends, click the journal button again to view and save your conversation notes.",
    highlight: ".journal-button",
    visual: {
      type: "icon-demo",
      action: "Click to Save Journal",
      iconColor: "#0b59f50a",
      bgColor: "#ebf5ffff",
      iconType: "save"
    }
  },
  {
    title: "Customize Settings",
    description: "Access the settings to change AI voices, adjust preferences, or sign out of your account.",
    highlight: ".settings-button",
    visual: {
      type: "icon-demo",
      action: "Click for Settings",
      iconColor: "#6b7280",
      bgColor: "#e7e7e7ff",
      iconType: "settings"
    }
  },
  {
    title: "Need Help Again?",
    description: "If you ever need to see these instructions again, just click the info icon to reopen this guide.",
    highlight: ".info-button",
    visual: {
      type: "icon-demo",
      action: "Click for Help",
      iconColor: "#757676ff",
      bgColor: "#cccdcd89",
      iconType: "info"
    }
  },
  {
    title: "Contact Support",
    description: "If you have questions or run into any issues contact us at support@preptune.com.",
    highlight: ".support-button",
    visual: {
      type: "icon-demo",
      action: "support@preptune.com",
      iconColor: "#0b59f50a",
      bgColor: "#ebf5ffff",
      iconType: "mail"
    }
  },
  {
    title: "Ready to Start?",
    description: "You're all set! Click below to begin your first conversation with Preptune.",
    highlight: ".ai-icon-io",
    visual: {
      type: "welcome-graphic",
      message: "Let's begin your journey!"
    }
  }
];
  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;

  // Confetti effect
  useEffect(() => {
    if (isLastStep && isVisible) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 7300);
      return () => clearTimeout(timer);
    }
  }, [currentStep, isVisible, isLastStep]);

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    onComplete();
  };

  const handleStartFirstCall = () => {
    onComplete();
    setTimeout(() => {
    }, 200);
  };

  // Add highlight effect to target elements
  useEffect(() => {
    if (!isVisible) return;

    const removeHighlights = () => {
      document.querySelectorAll('.guide-highlight').forEach(el => {
        el.classList.remove('guide-highlight');
      });
    };

    const addHighlight = (selector) => {
      removeHighlights();
      if (selector) {
        const element = document.querySelector(selector);
        if (element) {
          element.classList.add('guide-highlight');
        }
      }
    };

    addHighlight(currentStepData.highlight);

    return removeHighlights;
  }, [currentStep, isVisible, currentStepData.highlight]);

  const renderIcon = (iconType, color) => {
    switch (iconType) {
      case "phone":
        return (
          <div className="ai-icon">
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4.5 2C3.11929 2 2 3.11929 2 4.5C2 5.88072 3.11929 7 4.5 7C5.88072 7 7 5.88072 7 4.5C7 3.11929 5.88072 2 4.5 2ZM3 4.5C3 3.67157 3.67157 3 4.5 3C5.32843 3 6 3.67157 6 4.5C6 5.32843 5.32843 6 4.5 6C3.67157 6 3 5.32843 3 4.5ZM10.5 2C9.11929 2 8 3.11929 8 4.5C8 5.88072 9.11929 7 10.5 7C11.8807 7 13 5.88072 13 4.5C13 3.11929 11.8807 2 10.5 2ZM9 4.5C9 3.67157 9.67157 3 10.5 3C11.3284 3 12 3.67157 12 4.5C12 5.32843 11.3284 6 10.5 6C9.67157 6 9 5.32843 9 4.5ZM2 10.5C2 9.11929 3.11929 8 4.5 8C5.88072 8 7 9.11929 7 10.5C7 11.8807 5.88072 13 4.5 13C3.11929 13 2 11.8807 2 10.5ZM4.5 9C3.67157 9 3 9.67157 3 10.5C3 11.3284 3.67157 12 4.5 12C5.32843 12 6 11.3284 6 10.5C6 9.67157 5.32843 9 4.5 9ZM10.5 8C9.11929 8 8 9.11929 8 10.5C8 11.8807 9.11929 13 10.5 13C11.8807 13 13 11.8807 13 10.5C13 9.11929 11.8807 8 10.5 8ZM9 10.5C9 9.67157 9.67157 9 10.5 9C11.3284 9 12 9.67157 12 10.5C12 11.3284 11.3284 12 10.5 12C9.67157 12 9 11.3284 9 10.5Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path>
            </svg>
          </div>
        );
      case "phone-off":
        return (
          <div className="endcall-icon">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path stroke="white" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M5.693 16.013H7.31a1.685 1.685 0 0 0 1.685-1.684v-.645A1.684 1.684 0 0 1 10.679 12h2.647a1.686 1.686 0 0 1 1.686 1.686v.646c0 .446.178.875.494 1.19.316.317.693.495 1.14.495h1.685a1.556 1.556 0 0 0 1.597-1.016c.078-.214.107-.776.088-1.002.014-4.415-3.571-6.003-8-6.004-4.427 0-8.014 1.585-8.01 5.996-.02.227.009.79.087 1.003a1.558 1.558 0 0 0 1.6 1.02Z"/>
            </svg>
          </div>
        );
        
      case "journal":
        return (
         <div className="Journal-icon">
             <svg width="17" height="17" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 2.5C3 2.22386 3.22386 2 3.5 2H9.08579C9.21839 2 9.34557 2.05268 9.43934 2.14645L11.8536 4.56066C11.9473 4.65443 12 4.78161 12 4.91421V12.5C12 12.7761 11.7761 13 11.5 13H3.5C3.22386 13 3 12.7761 3 12.5V2.5ZM3.5 1C2.67157 1 2 1.67157 2 2.5V12.5C2 13.3284 2.67157 14 3.5 14H11.5C12.3284 14 13 13.3284 13 12.5V4.91421C13 4.51639 12.842 4.13486 12.5607 3.85355L10.1464 1.43934C9.86514 1.15804 9.48361 1 9.08579 1H3.5ZM4.5 4C4.22386 4 4 4.22386 4 4.5C4 4.77614 4.22386 5 4.5 5H7.5C7.77614 5 8 4.77614 8 4.5C8 4.22386 7.77614 4 7.5 4H4.5ZM4.5 7C4.22386 7 4 7.22386 4 7.5C4 7.77614 4.22386 8 4.5 8H10.5C10.7761 8 11 7.77614 11 7.5C11 7.22386 10.7761 7 10.5 7H4.5ZM4.5 10C4.22386 10 4 10.2239 4 10.5C4 10.7761 4.22386 11 4.5 11H10.5C10.7761 11 11 10.7761 11 10.5C11 10.2239 10.7761 10 10.5 10H4.5Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"/>
            </svg>
          </div>
        );
      case "save":
        return (
          <button className="summary-btn primary">
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 9.50006C1 10.3285 1.67157 11.0001 2.5 11.0001H4L4 10.0001H2.5C2.22386 10.0001 2 9.7762 2 9.50006L2 2.50006C2 2.22392 2.22386 2.00006 2.5 2.00006L9.5 2.00006C9.77614 2.00006 10 2.22392 10 2.50006V5.50006C10 5.77622 10.2239 6.00006 10.5 6.00006C10.7761 6.00006 11 5.77622 11 5.50006V2.50006C11 1.67163 10.3284 1.00006 9.5 1.00006H2.5C1.67157 1.00006 1 1.67163 1 2.50006V9.50006ZM13.8536 6.14652C14.0488 6.34178 14.0488 6.65836 13.8536 6.85362L10.8536 9.85362C10.6583 10.0489 10.3417 10.0489 10.1464 9.85362L8.64645 8.35362C8.45118 8.15836 8.45118 7.84178 8.64645 7.64652C8.84171 7.45125 9.15829 7.45125 9.35355 7.64652L10.5 8.79297L13.1464 6.14652C13.3417 5.95125 13.6583 5.95125 13.8536 6.14652Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"/>
            </svg>
            <span>Save</span>
          </button>
        );
      case "view":
        return (
          <button className="summary-btn primary">
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 2C2.44772 2 2 2.44772 2 3V12C2 12.5523 2.44772 13 3 13H12C12.5523 13 13 12.5523 13 12V8.5C13 8.22386 12.7761 8 12.5 8C12.2239 8 12 8.22386 12 8.5V12H3V3L6.5 3C6.77614 3 7 2.77614 7 2.5C7 2.22386 6.77614 2 6.5 2H3ZM12.8536 2.14645C12.9015 2.19439 12.9377 2.24964 12.9621 2.30861C12.9861 2.36669 12.9996 2.4303 13 2.497L13 2.5V2.50049V5.5C13 5.77614 12.7761 6 12.5 6C12.2239 6 12 5.77614 12 5.5V3.70711L6.85355 8.85355C6.65829 9.04882 6.34171 9.04882 6.14645 8.85355C5.95118 8.65829 5.95118 8.34171 6.14645 8.14645L11.2929 3H9.5C9.22386 3 9 2.77614 9 2.5C9 2.22386 9.22386 2 9.5 2H12.4999H12.5C12.5678 2 12.6324 2.01349 12.6914 2.03794C12.7504 2.06234 12.8056 2.09851 12.8536 2.14645Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"/>
            </svg>
            View
          </button>
        );
      case "mail":
        return (
          <button className="summary-btn primary">
            <svg class="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
              <path d="M11.209 3.816a1 1 0 0 0-1.966.368l.325 1.74a5.338 5.338 0 0 0-2.8 5.762l.276 1.473.055.296c.258 1.374-.228 2.262-.63 2.998-.285.52-.527.964-.437 1.449.11.586.22 1.173.75 1.074l12.7-2.377c.528-.1.418-.685.308-1.27-.103-.564-.636-1.123-1.195-1.711-.606-.636-1.243-1.306-1.404-2.051-.233-1.085-.275-1.387-.303-1.587-.009-.063-.016-.117-.028-.182a5.338 5.338 0 0 0-5.353-4.39l-.298-1.592Z"/>
              <path fill-rule="evenodd" d="M6.539 4.278a1 1 0 0 1 .07 1.412c-1.115 1.23-1.705 2.605-1.83 4.26a1 1 0 0 1-1.995-.15c.16-2.099.929-3.893 2.342-5.453a1 1 0 0 1 1.413-.069Z" clip-rule="evenodd"/>
              <path d="M8.95 19.7c.7.8 1.7 1.3 2.8 1.3 1.6 0 2.9-1.1 3.3-2.5l-6.1 1.2Z"/>
            </svg>
            Mail
          </button>
        );
        
      case "settings":
        return (
          <svg className="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="30" height="30" fill="none" viewBox="0 0 24 24">
            <path stroke="#5a5a5aff" strokeLinecap="square" strokeLinejoin="round" strokeWidth="1.5" d="M10 19H5a1 1 0 0 1-1-1v-1a3 3 0 0 1 3-3h2m10 1a3 3 0 0 1-3 3m3-3a3 3 0 0 0-3-3m3 3h1m-4 3a3 3 0 0 1-3-3m3 3v1m-3-4a3 3 0 0 1 3-3m-3 3h-1m4-3v-1m-2.121 1.879-.707-.707m5.656 5.656-.707-.707m-4.242 0-.707.707m5.656-5.656-.707.707M12 8a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/>
          </svg>
        );
      case "refresh":
        return (
          <button className="interrupt-icon-io">
<svg class="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
  <path stroke="currentColor" stroke-linecap="round" stroke-width="1.5" d="m6 6 12 12m3-6a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>
</svg>

          </button>
        );
      case "clock":
        return (
          <button className="ai-icon">
            <svg class="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
              <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>
            </svg>
          </button>
        );
      case "info":
        return (
          <div className="info-icon">
            <svg class="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="30" height="30" fill="#646464ff" viewBox="0 0 24 24">
              <path fill-rule="evenodd" d="M2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12Zm9.408-5.5a1 1 0 1 0 0 2h.01a1 1 0 1 0 0-2h-.01ZM10 10a1 1 0 1 0 0 2h1v3h-1a1 1 0 1 0 0 2h4a1 1 0 1 0 0-2h-1v-4a1 1 0 0 0-1-1h-2Z" clip-rule="evenodd"/>
            </svg>
          </div>
        );
      default:
        return null;
    }
  };

  const renderVisual = () => {
    const { visual } = currentStepData;
    
    if (!visual) return null;

    if (visual.type === "dual-icon-demo") {
      return (
        <div className="visual-demo">
          <div className="dual-icon-container">
            {visual.icons.map((iconData, index) => (
              <div key={index} className={`single-icon-demo ${index === 0 ? 'start-call' : 'end-call'}`}>
                <div 
                  className="demo-icon-container small"
                  style={{ backgroundColor: iconData.bgColor }}
                >
                  <div className="demo-icon-wrapper">
                    <div className="demo-icon">
                      {renderIcon(iconData.iconType, iconData.iconColor)}
                    </div>
                    <div className="demo-ripple" style={{ color: iconData.iconColor }}></div>
                  </div>
                </div>
                <p className="demo-action-text small">{iconData.action}</p>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (visual.type === "icon-demo") {
      return (
        <div className="visual-demo">
          <div 
            className="demo-icon-container"
            style={{ backgroundColor: visual.bgColor }}
          >
            <div className="demo-icon-wrapper">
              <div className="demo-icon">
                {renderIcon(visual.iconType, visual.iconColor)}
              </div>
              <div className="demo-ripple" style={{ color: visual.iconColor }}></div>
            </div>
          </div>
          <p className="demo-action-text">{visual.action}</p>
        </div>
      );
    }

    if (visual.type === "welcome-graphic") {
      return (
        <div className="welcome-visual">
          <p className="welcome-message">{visual.message}</p>
        </div>
      );
    }

    return null;
  };

  const ConfettiPiece = ({ delay, color, size }) => (
    <div 
      className="confetti-piece"
      style={{
        '--delay': delay,
        '--color': color,
        '--size': size,
        left: `${Math.random() * 100}%`,
        animationDelay: `${delay}s`
      }}
    />
  );

  const renderConfetti = () => {
    if (!showConfetti) return null;
    
    const colors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#feca57', '#48dbfb', '#ff9ff3'];
    const pieces = [];
    
    for (let i = 0; i < 50; i++) {
      pieces.push(
        <ConfettiPiece
          key={i}
          delay={Math.random() * 2}
          color={colors[Math.floor(Math.random() * colors.length)]}
          size={`${4 + Math.random() * 6}px`}
        />
      );
    }
    
    return <div className="confetti-container">{pieces}</div>;
  };

  if (!isVisible) return null;

  return (
    <>
      <div className="guide-overlay" />
      
      <div className="instruction-card">
        <button 
          className="instruction-close"
          onClick={handleComplete}
          aria-label="Close guide"
        >
          <X size={16} />
        </button>

        <div className="instruction-content">
          <div className="instruction-header">
            <h3>{currentStepData.title}</h3>
            <div className="step-indicator">
              {currentStep + 1} of {steps.length}
            </div>
          </div>
          
          {renderVisual()}
          
          <p className="instruction-text">
            {currentStepData.description}
          </p>

          <div className="instruction-nav">
            <button 
              className="nav-btn secondary"
              onClick={prevStep}
              disabled={currentStep === 0}
            >
              <ChevronLeft size={16} />
              Back
            </button>

            <div className="step-dots">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`step-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                />
              ))}
            </div>

            {isLastStep ? (
              <button 
                className="nav-btn primary final-begin-btn"
                onClick={handleStartFirstCall}
              >
                Begin
              </button>
            ) : (
              <button 
                className="nav-btn primary"
                onClick={nextStep}
              >
                Next
                <ChevronRight size={16} />
              </button>
            )}
          </div>
        </div>
        
      </div>
      {isLastStep && showConfetti && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          recycle={false}
          numberOfPieces={80}
        />
      )}
    </>
  );
};

export default FirstTimeUserGuide;